import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  applyWhenValue,
  email,
  form,
  FormField,
  maxLength,
  minLength,
  pattern,
  required,
  validate,
} from '@angular/forms/signals';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { map } from 'rxjs';
import {
  IdentityTypes,
  LoginIdetityUserDetails,
  UserUpdateRequest,
} from '../../../../core/api/mhsIdentityApiClient';
import { RolesLookup } from '../../../../core/context/roles-lookup';
import { UsertStore } from '../../../../core/context/userStore';
import { CompanyConfigurationService } from '../../../../core/services/company-configuration-service';
import { ResourceService } from '../../../../core/services/resource-service';
import { nullIfEmpty } from '../../../../core/utils/transformers';
import { FieldErrorsComponent } from '../../../../shared/ui/components/field-error/field-error';
import { UserService } from '../../services/user-service';
import { UserData } from '../user-create/user-data';

@Component({
  selector: 'app-user-edit',
  imports: [
    ReactiveFormsModule,
    FormField,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzSpinComponent,
    NzCheckboxModule,
    RouterLink,
    NzFlexModule,
    NzGridModule,
    NzDividerModule,
    NzIconModule,
    NzTagModule,
    CommonModule,
    NzAvatarModule,
    NzDropdownModule,
    FieldErrorsComponent,
  ],
  templateUrl: './user-edit.html',
  styleUrl: './user-edit.scss',
})
export class UserEditComponent {
  readonly medicalRoleNames = ['MEDICAL', 'TECHNICIAN', 'NURSE', 'MEDICAL_PRACTICIONER'];
  readonly roles = RolesLookup.roles;

  userService = inject(UserService);
  userStore = inject(UsertStore);
  resourceService = inject(ResourceService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  messageService = inject(NzMessageService);

  userId = Number(this.route.snapshot.paramMap.get('id'));
  loadedTenants = signal<string[]>([]);

  companyConfigurationService = inject(CompanyConfigurationService);
  companyConfiguration = toSignal(this.companyConfigurationService.getCompanyConfiguration(), {
    initialValue: null,
  });

  isLoadingUser = signal(true);
  isSubmitting = signal(false);
  submitted = signal(false);
  selectAllUnits = signal<boolean>(false);
  selectAllModules = signal<boolean>(false);

  identifierTypes = toSignal(
    this.resourceService
      .getResourceIdentityTypes()
      .pipe(map((types) => types.filter((type) => type.name == 'PESEL' || type.name == 'OTHER'))),
    { initialValue: [] },
  );
  medTitles = toSignal(this.resourceService.getMedTitles(), { initialValue: [] });
  medSpecializations = toSignal(this.resourceService.getMedSpecializations(), { initialValue: [] });
  medRoleIds = computed(() => {
    return (
      this.companyConfiguration()
        ?.avilableRoles?.filter((role) => this.medicalRoleNames.includes(role.normalizedName!))
        .map((role) => role.id) || []
    );
  });

  userEditModel = signal<UserData>({
    login: '',
    email: '',
    password: '',
    passwordConfirm: '',
    roleId: 1,
    selecteUnitIds: [1],
    firstName: '',
    secondName: '',
    lastName: '',
    identifierType: 'PESEL',
    identifierValue: '',
    phoneNumber: '',
    phoneNumberCountryCode: '+48',

    licenseNumber: '',
    medTitle: '',
    medSpecialty: '',
    defaultUnitId: this.companyConfiguration()?.structure?.units?.[0]?.id ?? 0,
    selectedModules: ['basic'],
    applicationAccess: ['mhs.scm.user'],
    selectedRoles: ['mhs.scm.user'],
  });

  unitRows = computed(() => {
    const units = this.companyConfiguration()?.structure?.units ?? [];
    const rows: Array<Array<{ id?: number; name?: string }>> = [];

    for (let i = 0; i < units.length; i += 3) {
      if (i + 3 > units.length) {
        rows.push(units.slice(i, units.length));
        break;
      }
      rows.push(units.slice(i, i + 3));
    }

    return rows;
  });

  loginForm = form(this.userEditModel, (userModel) => {
    required(userModel.login);
    minLength(userModel.login, 3, { message: 'Login powinien mieć przynajmniej 3 znaki' });
    maxLength(userModel.login, 20, { message: 'Login powinien mieć co najwyżej 20 znaków' });

    email(userModel.email);
    required(userModel.email);

    required(userModel.firstName);
    required(userModel.lastName);
    required(userModel.identifierType);
    required(userModel.identifierValue);
    required(userModel.phoneNumber);
    minLength(userModel.phoneNumber, 9, { message: 'Numer telefonu powinien mieć 9 znaków' });
    maxLength(userModel.phoneNumber, 9, { message: 'Numer telefonu powinien mieć 9 znaków' });
    pattern(userModel.phoneNumber, /^[0-9]+$/, {
      message: 'Pole może zawierać wyłącznie cyfry',
    });
    required(userModel.phoneNumberCountryCode);

    minLength(userModel.selecteUnitIds, 1, {
      message: 'Wybierz przynajmniej jedną placówkę',
    });

    required(userModel.licenseNumber, {
      message: 'Numer prawa wykonywania zawodu jest wymagany dla ról medycznych',
      when: ({ valueOf }) => this.medRoleIds().includes(valueOf(userModel.roleId)),
    });

    applyWhenValue(
      userModel,
      (v) => this.medRoleIds().includes(v.roleId),
      (u) => {
        minLength(u.licenseNumber, 7, {
          message: 'Numer prawa wykonywania zawodu powinien mieć przynajmniej 7 znaków',
        });
        maxLength(u.licenseNumber, 8, {
          message: 'Numer prawa wykonywania zawodu powinien mieć maksymalnie 8 znaków',
        });
      },
    );

    applyWhenValue(
      userModel,
      (v) => v.identifierType === 'PESEL',
      (u) => {
        minLength(u.identifierValue, 11, { message: 'PESEL powinien mieć 11 znaków' });
        maxLength(u.identifierValue, 11, { message: 'PESEL powinien mieć 11 znaków' });
        pattern(u.identifierValue, /^[0-9]+$/, {
          message: 'Pole może zawierać wyłącznie cyfry',
        });
      },
    );

    applyWhenValue(
      userModel,
      (v) => v.identifierType === 'OTHER',
      (u) => {
        minLength(u.identifierValue, 5, {
          message: 'Wartość identyfikatora powinna mieć przynajmniej 5 znaków',
        });
        maxLength(u.identifierValue, 20, {
          message: 'Wartość identyfikatora powinna mieć co najwyżej 20 znaków',
        });
      },
    );

    validate(userModel.passwordConfirm, ({ value, valueOf }) => {
      const passwordConfirm = value();
      const password = valueOf(userModel.password);

      if (password === passwordConfirm) {
        return null;
      }

      return {
        kind: 'passwordMismatch',
        message: 'Hasła muszą być takie same',
      };
    });
  });

  constructor() {
    this.loadUserData();
  }

  unitCheckboxToggle(unitId: number, checked: boolean) {
    const selectedUnitIds = this.loginForm.selecteUnitIds().value();

    if (checked) {
      const newSelectedUnitIds = [...selectedUnitIds, unitId];
      this.userEditModel.update((model) => ({ ...model, selecteUnitIds: newSelectedUnitIds }));
      return;
    }

    const filteredUnitIds = selectedUnitIds.filter((id) => id !== unitId);
    this.userEditModel.update((model) => ({ ...model, selecteUnitIds: filteredUnitIds }));
  }

  toggleSelectAllUnits() {
    const newValue = !this.selectAllUnits();
    this.selectAllUnits.set(newValue);

    if (newValue) {
      const allUnitIds =
        this.companyConfiguration()
          ?.structure?.units?.map((unit) => unit.id!)
          .filter(Boolean) || [];
      this.userEditModel.update((model) => ({ ...model, selecteUnitIds: allUnitIds }));
      return;
    }

    this.userEditModel.update((model) => ({ ...model, selecteUnitIds: [] }));
  }

  toggleSelectAllModules() {
    const newValue = !this.selectAllModules();
    this.selectAllModules.set(newValue);

    if (newValue) {
      const allModuleNames =
        this.companyConfiguration()
          ?.avilableModules?.map((module) => module.name!)
          .filter(Boolean) || [];
      this.userEditModel.update((model) => ({ ...model, selectedModules: allModuleNames }));
      return;
    }

    this.userEditModel.update((model) => ({ ...model, selectedModules: [] }));
  }

  moduleCheckboxToggle(moduleName: string, checked: boolean) {
    const selectedModules = this.loginForm.selectedModules().value();

    if (checked) {
      const newSelectedModules = [...selectedModules, moduleName];
      this.userEditModel.update((model) => ({ ...model, selectedModules: newSelectedModules }));
      return;
    }

    const filteredModules = selectedModules.filter((name) => name !== moduleName);
    this.userEditModel.update((model) => ({ ...model, selectedModules: filteredModules }));
  }

  roleCheckboxToggle(roleName: string, checked: boolean) {
    const selectedRoles = this.loginForm.selectedRoles().value();

    if (checked) {
      const newSelectedRoles = [...selectedRoles, roleName];
      this.userEditModel.update((model) => ({ ...model, selectedRoles: newSelectedRoles }));
      return;
    }

    const filteredRoles = selectedRoles.filter((name) => name !== roleName);
    this.userEditModel.update((model) => ({ ...model, selectedRoles: filteredRoles }));
  }

  setMedSpecialization(specialization: string) {
    this.userEditModel.update((model) => ({ ...model, medSpecialty: specialization }));
  }

  markAllTouchedAndDirty(node: unknown) {
    const control = node as {
      markAsTouched?: () => void;
      markAsDirty?: () => void;
      controls?: Record<string, unknown>;
      fields?: Record<string, unknown>;
      children?: Record<string, unknown>;
    };

    control.markAsTouched?.();
    control.markAsDirty?.();

    const children = control.controls ?? control.fields ?? control.children ?? null;
    if (!children) {
      return;
    }

    Object.values(children).forEach((child) => {
      this.markAllTouchedAndDirty(child);
    });
  }

  submit() {
    this.submitted.set(true);
    this.markAllTouchedAndDirty(this.loginForm());

    if (!this.loginForm().valid()) {
      this.isSubmitting.set(false);
      return;
    }

    this.isSubmitting.set(true);

    this.userService
      .update(this.mapToApiModel(this.userEditModel()))
      .subscribe(
        () => {
          this.messageService.success('Dane użytkownika zostały zaktualizowane');
          this.router.navigate(['/users', this.userId, 'details']);
        },
        (error) => {
          console.error('Error updating user:', error);
        },
      )
      .add(() => {
        this.isSubmitting.set(false);
      });
  }

  private loadUserData(): void {
    if (!Number.isFinite(this.userId)) {
      this.messageService.error('Niepoprawny identyfikator użytkownika');
      this.router.navigate(['/users']);
      this.isLoadingUser.set(false);
      return;
    }

    this.userService
      .get(this.userId)
      .subscribe(
        (user) => {
          const mappedUser = this.mapFromApiModel(user);
          this.userEditModel.set(mappedUser);
          this.syncSelectAllState(mappedUser);
        },
        (error) => {
          console.error('Error loading user for edit:', error);
          this.messageService.error('Nie udało się pobrać danych użytkownika');
          this.router.navigate(['/users']);
        },
      )
      .add(() => {
        this.isLoadingUser.set(false);
      });
  }

  private mapFromApiModel(user: LoginIdetityUserDetails): UserData {
    const parsedPhone = this.parsePhone(user.phone);
    const selectedModules = this.parseClaimCollection(user.claims?.['mhs.scm.modules']);
    const selectedTenants = this.parseClaimCollection(user.claims?.['mhs.scm.tenants']);

    if (selectedTenants.length > 0) {
      this.loadedTenants.set(selectedTenants);
    }

    const selectedRoles = (user.roles ?? []).filter((roleName) =>
      this.roles.some((role) => role.name === roleName),
    );
    return {
      login: user.ssoLogin ?? '',
      email: user.email ?? '',
      password: '',
      passwordConfirm: '',
      roleId: user.roleId ?? this.companyConfiguration()?.avilableRoles?.[0]?.id ?? 0,
      selecteUnitIds: user.unitIds ?? [],
      firstName: user.firstName ?? '',
      secondName: user.secondName ?? '',
      lastName: user.lastName ?? '',
      identifierType: String(user.identifierType ?? 'PESEL'),
      identifierValue: user.identifierValue ?? '',
      phoneNumber: parsedPhone.phoneNumber,
      phoneNumberCountryCode: parsedPhone.countryCode,
      licenseNumber: user.licenceNumber ?? '',
      medTitle: user.title ?? '',
      medSpecialty: user.specialisation ?? '',
      defaultUnitId: Number(user.defaultUnitId ?? 0),
      selectedModules,
      applicationAccess: selectedTenants,
      selectedRoles: selectedRoles.length > 0 ? selectedRoles : ['mhs.scm.user'],
    };
  }

  private mapToApiModel(userData: UserData): UserUpdateRequest {
    const request = new UserUpdateRequest();
    const roleName = this.companyConfiguration()?.avilableRoles?.find(
      (role) => role.id === userData.roleId,
    )?.name;
    const currentTenant = this.userStore.currentTenant();
    const tenants =
      this.loadedTenants().length > 0 ? this.loadedTenants() : [currentTenant?.tenantName!];

    request.id = this.userId;
    request.roleId = userData.roleId;
    request.roleName = nullIfEmpty(roleName) ?? undefined;
    request.unitIds = userData.selecteUnitIds;
    request.tenants = tenants.filter(Boolean);
    request.firstName = userData.firstName;
    request.secondName = nullIfEmpty(userData.secondName) ?? undefined;
    request.lastName = userData.lastName;
    request.email = userData.email;
    request.phone = `${userData.phoneNumberCountryCode}${userData.phoneNumber}`;
    request.defaultUnitId = userData.defaultUnitId;
    request.identifierType = userData.identifierType as IdentityTypes;
    request.identifierValue = userData.identifierValue;
    request.title = nullIfEmpty(userData.medTitle) ?? undefined;
    request.licenceNumber = nullIfEmpty(userData.licenseNumber) ?? undefined;
    request.specialisation = nullIfEmpty(userData.medSpecialty) ?? undefined;
    request.internalRoles = userData.selectedRoles;
    request.modules = userData.selectedModules;

    return request;
  }

  private parseClaimCollection(value?: string): string[] {
    if (!value) {
      return [];
    }

    return value
      .replace(/[\[\]'\"]/g, '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  }

  private parsePhone(value?: string): { countryCode: string; phoneNumber: string } {
    if (!value) {
      return {
        countryCode: '+48',
        phoneNumber: '',
      };
    }
    if (!value.startsWith('+') || value.length <= 9) {
      return {
        countryCode: '+48',
        phoneNumber: value,
      };
    }
    const prefix = value.slice(0, 3);
    const number = value.slice(3);

    return {
      countryCode: prefix,
      phoneNumber: number,
    };
  }

  private syncSelectAllState(userData: UserData): void {
    const unitsCount = this.companyConfiguration()?.structure?.units?.length ?? 0;
    const modulesCount = this.companyConfiguration()?.avilableModules?.length ?? 0;

    this.selectAllUnits.set(unitsCount > 0 && userData.selecteUnitIds.length >= unitsCount);
    this.selectAllModules.set(modulesCount > 0 && userData.selectedModules.length >= modulesCount);
  }
}
